import { ChatMessage, SocketChatSession, UserRole } from '../types';

/** Minimum gap between consecutive customer messages. */
export const CLIENT_MESSAGE_COOLDOWN_MS = 1_000;

export type ClientMessageQueueBlockReason = 'awaiting_ai' | 'cooldown';

export type QueuedClientMessage = {
  content: string;
  files: File[];
  /** Why this message was queued — controls when it may auto-flush. */
  blockReason: ClientMessageQueueBlockReason;
};

export class ClientMessageQueueHelper {
  static isBotPhase(session: SocketChatSession | null | undefined): boolean {
    const hasLiveChatStarted = Boolean(session?.assign?.start);
    const isQueuedForLiveAgent = Boolean(session?.liveChatRedirect) && !hasLiveChatStarted;
    return !hasLiveChatStarted && !isQueuedForLiveAgent;
  }

  /** True while the last transcript message is from the customer (AI reply still pending). */
  static isAwaitingBotResponse(
    session: SocketChatSession | null | undefined,
    messages: ChatMessage[]
  ): boolean {
    if (!this.isBotPhase(session)) {
      return false;
    }

    const lastMessage = messages.length ? messages[messages.length - 1] : null;
    return Boolean(lastMessage && lastMessage.sender?.role === UserRole.Client);
  }

  static getMsUntilCooldownEnds(
    lastClientMessageSentAt: number | null | undefined,
    nowMs: number
  ): number {
    if (lastClientMessageSentAt == null) {
      return 0;
    }

    return Math.max(0, lastClientMessageSentAt + CLIENT_MESSAGE_COOLDOWN_MS - nowMs);
  }

  static isInMessageCooldown(
    lastClientMessageSentAt: number | null | undefined,
    nowMs: number
  ): boolean {
    return this.getMsUntilCooldownEnds(lastClientMessageSentAt, nowMs) > 0;
  }

  /** Prefer awaiting-AI when both apply — flush as soon as the bot replies (then honor any remaining 1s gap). */
  static getSendBlockReason(params: {
    session: SocketChatSession | null | undefined;
    messages: ChatMessage[];
    lastClientMessageSentAt: number | null | undefined;
    nowMs: number;
  }): ClientMessageQueueBlockReason | null {
    if (this.isAwaitingBotResponse(params.session, params.messages)) {
      return 'awaiting_ai';
    }

    if (this.isInMessageCooldown(params.lastClientMessageSentAt, params.nowMs)) {
      return 'cooldown';
    }

    return null;
  }

  static isSendBlocked(params: {
    session: SocketChatSession | null | undefined;
    messages: ChatMessage[];
    lastClientMessageSentAt: number | null | undefined;
    nowMs: number;
  }): boolean {
    return this.getSendBlockReason(params) !== null;
  }

  /**
   * Flush when the original block is cleared, and never sooner than the 1s send gap.
   * - awaiting_ai → bot has replied, and 1s since last send
   * - cooldown → 1s since last send
   */
  static canFlushQueuedMessage(params: {
    blockReason: ClientMessageQueueBlockReason;
    session: SocketChatSession | null | undefined;
    messages: ChatMessage[];
    lastClientMessageSentAt: number | null | undefined;
    nowMs: number;
  }): boolean {
    if (this.isInMessageCooldown(params.lastClientMessageSentAt, params.nowMs)) {
      return false;
    }

    if (params.blockReason === 'awaiting_ai') {
      return !this.isAwaitingBotResponse(params.session, params.messages);
    }

    return true;
  }

  static getLastClientMessageSentAt(messages: ChatMessage[]): number | null {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.sender?.role === UserRole.Client) {
        const created = messages[i].created;
        const time = created instanceof Date ? created.getTime() : new Date(created).getTime();
        return Number.isFinite(time) ? time : null;
      }
    }
    return null;
  }
}
